<div class="container-fluid p-0 profile-page">

    <!-- HEADER -->
    <HeaderSection
        :breadcrumb="breadcrumb"
        @breadcrumb-click="handleBreadcrumbClick"
        @open-assistant="showAssistant = true"
    />

    <!-- LOADING STATE -->
    <div v-if="loading" class="container text-center py-5">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-white mt-2">Loading profile...</p>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="error" class="container py-5">
        <div class="alert alert-danger text-center">
            {{ error }}
        </div>
    </div>

    <!-- PROFILE CONTENT -->
    <div v-else>
        <section class="container py-4 profile-shell">
            <div class="profile-hero-card">
                <div class="profile-hero-grid">
                    <div class="profile-hero-main">
                        <div class="profile-avatar profile-avatar-lg d-flex align-items-center justify-content-center">
                            <span class="profile-avatar-text">{{ avatar_initials }}</span>
                        </div>

                        <div class="profile-hero-copy">
                            <span class="profile-kicker">Personal profile</span>
                            <h2 class="text-white mb-2 profile-hero-name">{{ fullName }}</h2>
                            <p class="text-white-50 mb-0 profile-hero-meta">{{ userRole }} • Member since {{ joinYear }}</p>

                            <div class="profile-meta-pills">
                                <span class="profile-meta-pill">
                                    <i class="bi bi-envelope me-2"></i>{{ userEmail }}
                                </span>
                                <span class="profile-meta-pill">
                                    <i class="bi bi-telephone me-2"></i>{{ userPhone }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="profile-hero-side">
                        <div class="profile-score-card">
                            <span class="profile-score-label">Completion score</span>
                            <div class="profile-score-value">{{ profileProgress }}%</div>
                            <small class="text-white-50">
                                {{ remainingItems }} item{{ remainingItems !== 1 ? 's' : '' }} left to complete your profile
                            </small>
                        </div>

                        <router-link
                            to="/edit_profile"
                            class="btn btn-primary profile-edit-btn text-decoration-none"
                        >
                            <i class="bi bi-pencil-square me-2"></i>Edit Profile
                        </router-link>
                    </div>
                </div>
            </div>

            <div class="row g-4 mt-1">
                <div class="col-lg-5">
                    <div class="card profile-surface-card p-4 rounded-4 h-100">
                        <div class="profile-section-heading">
                            <span class="profile-section-eyebrow">Reachable details</span>
                            <h5 class="text-white mb-0">Contact Information</h5>
                        </div>

                        <div class="profile-contact-stack">
                            <div class="profile-contact-item">
                                <div class="icon-box profile-icon-box">
                                    <i class="bi bi-envelope-fill text-white"></i>
                                </div>
                                <div>
                                    <span class="profile-contact-label">Email address</span>
                                    <div class="text-white profile-contact-value">{{ userEmail }}</div>
                                </div>
                            </div>

                            <div class="profile-contact-item">
                                <div class="icon-box profile-icon-box">
                                    <i class="bi bi-telephone-fill text-white"></i>
                                </div>
                                <div>
                                    <span class="profile-contact-label">Phone number</span>
                                    <div class="text-white profile-contact-value">{{ userPhone }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-lg-7">
                    <div class="card profile-surface-card p-4 rounded-4 h-100">
                        <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
                            <div class="profile-section-heading mb-0">
                                <span class="profile-section-eyebrow">Momentum</span>
                                <h5 class="text-white mb-0">Profile Progress</h5>
                            </div>
                            <div class="profile-progress-badge">{{ profileProgress }}%</div>
                        </div>

                        <div class="progress rounded-pill mb-3 progress-lg profile-progress-track">
                            <div
                                class="progress-bar progress-bar-custom profile-progress-bar"
                                :style="{ width: profileProgress + '%' }"
                            ></div>
                        </div>

                        <div class="profile-progress-copy">
                            <strong class="text-white">Almost there.</strong>
                            <span class="text-white-50">
                                Complete {{ remainingItems }} more item{{ remainingItems !== 1 ? 's' : '' }} to unlock a full profile.
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card profile-surface-card p-4 rounded-4 mt-4">
                <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-2 mb-4">
                    <div class="profile-section-heading mb-0">
                        <span class="profile-section-eyebrow">Checklist</span>
                        <h5 class="text-white mb-0">Complete Your Profile</h5>
                    </div>
                    <p class="text-white-50 mb-0 profile-checklist-copy">Keep your information current so your account is easier to verify and manage.</p>
                </div>

                <div class="row g-3">
                    <div class="col-md-6 col-xl-3">
                        <div class="completion-item profile-check-card" :class="{ 'incomplete': !isEmailVerified }">
                            <div class="profile-check-status">
                                <i
                                    class="bi"
                                    :class="isEmailVerified ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
                                ></i>
                                <span class="profile-check-badge" :class="isEmailVerified ? 'is-complete' : 'is-pending'">
                                    {{ isEmailVerified ? 'Done' : 'Pending' }}
                                </span>
                            </div>
                            <h6 class="text-white mb-1">{{ isEmailVerified ? 'Email Verified' : 'Verify Email' }}</h6>
                            <p class="text-white-50 mb-0">Your primary sign-in address is confirmed.</p>
                        </div>
                    </div>

                    <div class="col-md-6 col-xl-3">
                        <div class="completion-item profile-check-card" :class="{ 'incomplete': !hasPhoneNumber }">
                            <div class="profile-check-status">
                                <i
                                    class="bi"
                                    :class="hasPhoneNumber ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
                                ></i>
                                <span class="profile-check-badge" :class="hasPhoneNumber ? 'is-complete' : 'is-pending'">
                                    {{ hasPhoneNumber ? 'Done' : 'Pending' }}
                                </span>
                            </div>
                            <h6 class="text-white mb-1">{{ hasPhoneNumber ? 'Phone Added' : 'Add Phone Number' }}</h6>
                            <p class="text-white-50 mb-0">Add a mobile number for faster account recovery.</p>
                        </div>
                    </div>

                    <div class="col-md-6 col-xl-3">
                        <div class="completion-item profile-check-card" :class="{ 'incomplete': !userProfile?.first_name }">
                            <div class="profile-check-status">
                                <i
                                    class="bi"
                                    :class="userProfile?.first_name ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
                                ></i>
                                <span class="profile-check-badge" :class="userProfile?.first_name ? 'is-complete' : 'is-pending'">
                                    {{ userProfile?.first_name ? 'Done' : 'Pending' }}
                                </span>
                            </div>
                            <h6 class="text-white mb-1">{{ userProfile?.first_name ? 'First Name Added' : 'Add First Name' }}</h6>
                            <p class="text-white-50 mb-0">Make your account easier to identify across the workspace.</p>
                        </div>
                    </div>

                    <div class="col-md-6 col-xl-3">
                        <div class="completion-item profile-check-card" :class="{ 'incomplete': !userProfile?.last_name }">
                            <div class="profile-check-status">
                                <i
                                    class="bi"
                                    :class="userProfile?.last_name ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
                                ></i>
                                <span class="profile-check-badge" :class="userProfile?.last_name ? 'is-complete' : 'is-pending'">
                                    {{ userProfile?.last_name ? 'Done' : 'Pending' }}
                                </span>
                            </div>
                            <h6 class="text-white mb-1">{{ userProfile?.last_name ? 'Last Name Added' : 'Add Last Name' }}</h6>
                            <p class="text-white-50 mb-0">A full name helps with trust, records, and support requests.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    </div>

    <!-- Infinity Assistant -->
    <BolideAssistant :is_open="showAssistant" @close-assistant="showAssistant = false" />

</div>
